// src/Page/CreateQuestionBank.jsx

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../Component/SideBar";
import ReactDOM from "react-dom/client";
import {
  Box,
  Typography,
  TextField,
  Tooltip,
  IconButton,
  Paper,
  useTheme,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import {
  fetchExamData,
  deleteExamById,
  fetchExamDetailsById,
} from "../Api/examApi";

export default function CreateQuestionBank() {
  const [searchQuery, setSearchQuery] = useState("");
  const [examData, setExamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    id: null,
    loading: false,
    data: null,
  });

  const tableRef = useRef(null);
  const tableInstance = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();

  // Fetch exam details for preview
  useEffect(() => {
    const fetchDetails = async () => {
      if (previewDialog.open && previewDialog.id) {
        try {
          setPreviewDialog((prev) => ({ ...prev, loading: true }));
          const details = await fetchExamDetailsById(previewDialog.id);
          setPreviewDialog((prev) => ({
            ...prev,
            loading: false,
            data: details,
          }));
        } catch (err) {
          console.error("Error fetching exam details:", err);
          setPreviewDialog((prev) => ({
            ...prev,
            loading: false,
            data: null,
          }));
        }
      }
    };
    fetchDetails();
  }, [previewDialog.open, previewDialog.id]);

  // Fetch exam data
  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const response = await fetchExamData();
        const formatted = response.map((item, index) => ({
          ...item,
          testDate: item.testDate?.split("T")[0] || "-",
          index: index + 1,
        }));
        setExamData(formatted);
      } catch (error) {
        console.error("Failed to fetch exam data:", error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  // Initialize or update Tabulator
  useEffect(() => {
    if (!loading && tableRef.current) {
      if (!tableInstance.current) {
        setTimeout(() => {
          tableInstance.current = new Tabulator(tableRef.current, {
            data: examData,
            layout: "fitColumns",
            responsiveLayout: "hide",
            pagination: "local",
            paginationSize: 10,
            paginationSizeSelector: [5, 10, 20],
            movableColumns: true,
            autoResize: true,
            placeholder: "No test papers found.",
            columns: [
              {
                title: "Name",
                field: "name",
                headerFilter: "input",
                formatter: (cell) => {
                  const { id, name, status } = cell.getRow().getData();
                  const el = cell.getElement();
                  el.innerHTML = "";
                  const root = ReactDOM.createRoot(el);
                  root.render(
                    <Typography
                      variant="body2"
                      sx={{
                        color: status === 2 ? "#1976d2" : "#999",
                        fontWeight: "bold",
                        cursor: status === 2 ? "pointer" : "default",
                        wordBreak: "break-word",
                      }}
                      onClick={() => {
                        if (status === 2) navigate(`/exam/${id}`);
                      }}
                    >
                      {name}
                    </Typography>
                  );
                },
              },
              { title: "Subject", field: "subject", headerFilter: "input" },
              { title: "Test Date", field: "testDate", headerFilter: "input" },
              {
                title: "Status",
                field: "status",
                headerFilter: "select",
                headerFilterParams: {
                  values: {
                    1: "In Progress",
                    2: "Active",
                    3: "Inactive",
                  },
                },
                formatter: (cell) => {
                  const value = cell.getValue();
                  const statusMap = {
                    1: { label: "In Progress", color: "#fbc02d" },
                    2: { label: "Active", color: "#388e3c" },
                    3: { label: "Inactive", color: "#d32f2f" },
                  };
                  const status = statusMap[value] || {
                    label: "Unknown",
                    color: "#9e9e9e",
                  };
                  return `<span style="color: ${status.color}; font-weight: bold;">${status.label}</span>`;
                },
              },
              {
                title: "Action",
                field: "id",
                hozAlign: "center",
                width: 280,
                formatter: (cell) => {
                  const rowData = cell.getRow().getData();
                  const el = cell.getElement();
                  el.innerHTML = "";
                  const root = ReactDOM.createRoot(el);

                  const isDeletable = rowData.status === 1;

                  root.render(
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      justifyContent="center"
                      gap={1}
                    >
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleUpdate(rowData)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={isDeletable ? "Delete" : "Cannot delete"}>
                        <span>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => isDeletable && handleDelete(rowData)}
                            disabled={!isDeletable}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Preview">
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={() =>
                            navigate(`/exam/preview/${rowData.id}`)
                          }
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                },
              },
              {
                title: "Taken By",
                field: "attempts",
                hozAlign: "center",
                width: 120,
                formatter: (cell) => {
                  const rowData = cell.getRow().getData();
                  const el = cell.getElement();
                  el.innerHTML = "";
                  const root = ReactDOM.createRoot(el);

                  const isDisabled = !(
                    rowData.status === 2 || rowData.status === 3
                  );

                  root.render(
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      disabled={isDisabled}
                      onClick={() => {
                        if (!isDisabled) {
                          navigate(`/exam/${rowData.id}/attempts`);
                        }
                      }}
                    >
                      View
                    </Button>
                  );
                },
              },
            ],
          });
        }, 0);
      } else {
        tableInstance.current.replaceData(examData);
      }
    }
  }, [examData, loading, navigate]);

  // Search functionality
  useEffect(() => {
    if (tableInstance.current) {
      if (!searchQuery.trim()) {
        tableInstance.current.clearFilter();
      } else {
        tableInstance.current.setFilter([
          [
            { field: "name", type: "like", value: searchQuery },
            { field: "subject", type: "like", value: searchQuery },
            { field: "testDate", type: "like", value: searchQuery },
          ],
        ]);
      }
    }
  }, [searchQuery]);

  const handleUpdate = (row) => navigate(`/questionbank/edit/${row.id}`);

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this exam?")) return;
    try {
      await deleteExamById(row.id);
      const response = await fetchExamData();
      const formatted = response.map((item, index) => ({
        ...item,
        testDate: item.testDate?.split("T")[0] || "-",
        index: index + 1,
      }));
      setExamData(formatted);
    } catch (error) {
      console.error("Error deleting exam:", error);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
      <SideBar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: "55px",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Typography variant="h5" gutterBottom>
          Test Papers
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Tooltip title="Search by name, Subject, or Test Date" arrow>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Tooltip>
          </Grid>
        </Grid>

        <Paper
          elevation={3}
          sx={{
            width: "100%",
            borderRadius: 2,
            p: { xs: 1, md: 2 },
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#1e1e1e" : "#fafafa",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflowX: "auto", // Added to allow horizontal scroll
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Box sx={{ minWidth: { xs: "700px", sm: "600px", md: "100%" } }}>
                <div ref={tableRef} style={{ width: "100%" }} />
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
